/**
 * Chat upsert builder — the boundary between chat-conversation entry points
 * (realtime chat, inbox compose, conversation page) and the HTTP route
 * `app/api/chat/upsert/route.ts`.
 *
 *   components/realtime-chat.tsx                ┐
 *   components/chat/ChatCompose.tsx             │
 *   components/chat/ChatComposeTab.tsx          ├─ intent ─▶ upsertChatRoom
 *   components/chat/ChatInboxList.tsx           │                 │
 *   components/chat/ChatConversationPage.tsx    ┘                 ├── safeParse (boundary)
 *                                                                 └── fetch
 *
 * Why this exists:
 *  - Pre-extract there were 11 inline `fetch('/api/chat/upsert', …)` call-sites
 *    across 6 web files (counted 2026-06-12; the previous follow-up queue
 *    said 10/4). Sibling-E batch 1 (`.tondocs/RELIABILITY_ROADMAP.md §3a B1`)
 *    consolidates them so a Walking Skeleton test can drive producer +
 *    consumer in one call (no hand-coded body).
 *  - Mobile (`apps/mobile/lib/api-messaging.ts upsertChatConversation`)
 *    already shipped the same wire shape; a type-only `satisfies
 *    ChatUpsertRequest` pin on the mobile body literal closes the cross-repo
 *    drift door.
 *
 * Design notes:
 *  - Wire shape is intentionally permissive. The route does NOT canonicalise
 *    `roomName` (the sibling `chat/send` route DOES sort `:`-joined ids), so
 *    canonicalisation stays in the caller — different producers canonicalise
 *    differently and the builder is byte-truthful to each one.
 *  - Fire-and-forget vs await: callers decide. ChatCompose / ChatComposeTab /
 *    ChatInboxList fire-and-forget with `.catch(() => {})`; realtime-chat
 *    and ChatConversationPage `await`. The builder returns a Promise and
 *    does not impose either policy.
 *  - `credentials: 'same-origin'` was set explicitly on most legacy callers
 *    and omitted on a few. Spec default is `same-origin`, so omitting on the
 *    builder is wire-identical to either form.
 *
 * @see app/api/chat/upsert/route.ts — handler (boundary safeParse landed
 *      with this PR; same `VALIDATION_ERROR` shape as chat/send).
 * @see apps/mobile/lib/api-messaging.ts upsertChatConversation — mobile sibling
 * @see packages/contract/src/chat-upsert.ts — ChatUpsertRequestSchema
 * @see lib/chat/chat-send.ts — sibling builder (Sibling-E B2)
 * @see lib/offers/offer-builders.ts — pattern reference (G16/G18)
 * @see .tondocs/RELIABILITY_ROADMAP.md §3a Sibling-E batch 1
 * @see .tondocs/skills/hex.md §2 — Functional Core / Imperative Shell
 */

import {
  ChatUpsertRequestSchema,
  seal,
  type ChatUpsertRequest,
} from "@kraahl/contract"
import { postJson } from "@/lib/api/post-json"

/**
 * Thrown when the request body fails the contract Zod parse before fetch.
 * Programmer-bug signal: caller assembled an intent the contract does not
 * accept. The server would 400 anyway; this surfaces it at the responsible
 * line. Mirrors `OfferContractError` / `ChatLinkOrderContractError`.
 */
export class ChatUpsertContractError extends Error {
  readonly issues: ReadonlyArray<{ path: string; message: string }>
  constructor(issues: ReadonlyArray<{ path: string; message: string }>) {
    super(
      `[chat/upsert] contract parse failed: ${issues
        .map((i) => `${i.path}: ${i.message}`)
        .join("; ")}`,
    )
    this.name = "ChatUpsertContractError"
    this.issues = issues
  }
}

export interface UpsertChatRoomIntent {
  /**
   * Room name as the caller wants it stored. The route does NOT sort or
   * normalise — pass the canonical form the caller already computed.
   */
  roomName: string
  /**
   * Participant ids (typically `roomName.split(':')`, sometimes pre-sorted,
   * sometimes paired explicitly). At least one is required.
   */
  participantIds: string[]
}

export interface UpsertChatRoomResult {
  ok: boolean
  status: number
  /** `{ id: string }` on success; opaque `unknown` envelope on failure. */
  data: unknown
}

async function readJson(res: Response): Promise<unknown> {
  return res.json().catch(() => null)
}

/**
 * Build, safeParse, and POST `/api/chat/upsert`. Returns the response
 * envelope — does NOT throw on non-2xx (the callers either fire-and-forget
 * with `.catch()` or treat upsert as best-effort before send). Throws
 * `ChatUpsertContractError` on contract parse failure.
 */
export async function upsertChatRoom(
  intent: UpsertChatRoomIntent,
): Promise<UpsertChatRoomResult> {
  const body = {
    roomName: intent.roomName,
    participantIds: intent.participantIds,
  } satisfies ChatUpsertRequest

  const parsed = ChatUpsertRequestSchema.safeParse(body)
  if (!parsed.success) {
    throw new ChatUpsertContractError(
      parsed.error.issues.map((i) => ({
        path: i.path.join("."),
        message: i.message,
      })),
    )
  }

  // Sibling-E §4 #3c: `seal` after safeParse converts the body into a
  // `Built<ChatUpsertRequest>`. `postJson` accepts ONLY `Built<T>` — a
  // hand-rolled literal in a component cannot reach this seam.
  const res = await postJson("/api/chat/upsert", seal<ChatUpsertRequest>(body))
  return { ok: res.ok, status: res.status, data: await readJson(res) }
}
